import { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import { useRepositories } from '../../../infrastructure/http/RepositoryProvider';
import type { DeviceShare, PendingQrInvite, ShareLevel } from '../../../domain/DeviceShare';

export function useDeviceShareCard(deviceId: string) {
  const { deviceShares } = useRepositories();

  const [shares, setShares] = useState<DeviceShare[]>([]);
  const [pendingQr, setPendingQr] = useState<PendingQrInvite | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [email, setEmail] = useState('');
  const [level, setLevel] = useState<ShareLevel>('view');
  const [busy, setBusy] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [qrExpiresAt, setQrExpiresAt] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const { shares: found, pendingQrInvite } = await deviceShares.listShares(deviceId);
      setShares(found);
      setPendingQr(pendingQrInvite);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron cargar los accesos compartidos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [deviceId]);

  const handleInvite = async () => {
    if (!email.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await deviceShares.inviteByEmail(deviceId, email.trim(), level);
      setEmail('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo compartir el dispositivo.');
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (shareId: string) => {
    setError(null);
    try {
      await deviceShares.revokeShare(deviceId, shareId);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo revocar el acceso.');
    }
  };

  const handleGenerateQr = async () => {
    setBusy(true);
    setError(null);
    try {
      const { token, expiresAt } = await deviceShares.createQrInvite(deviceId, level);
      const url = `${window.location.origin}/join?token=${token}`;
      const dataUrl = await QRCode.toDataURL(url, { margin: 1, width: 240 });
      setQrDataUrl(dataUrl);
      setQrExpiresAt(expiresAt);
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo generar el código QR.');
    } finally {
      setBusy(false);
    }
  };

  return {
    shares,
    pendingQr,
    loading,
    error,
    email,
    setEmail,
    level,
    setLevel,
    busy,
    qrDataUrl,
    setQrDataUrl,
    qrExpiresAt,
    handleInvite,
    handleRevoke,
    handleGenerateQr,
  };
}
